import Keycloak from "keycloak-js";
import { cockpitKeycloakConfig, controlKeycloakConfig } from "./keycloak-config";
import { loadStoredOrFetchConfiguration } from "@newsenec/configuration-provider";
import { isHostControl } from "../urlUtils";
import { isCockpitItaly } from "../auth-provider";

export interface KeycloakClientProvider {
    isInitialized: () => boolean;

    /**
     * Returns the default keycloak client or initializes a new client if none exists.
     * This automatically checks the SSO, whether the user is authenticated already.
     */
    getInitializedClient: () => Promise<Keycloak>;
}

let keycloak: Keycloak | undefined;

const TOKEN_REFRESH_INTERVAL_MS = -1;

const getRedirectUri = (locale: string): string => `${window.location.origin}?kc_locale=${locale}`;

// Refresh token expiry timestamp is in seconds since epoch, while Date.now() returns milliseconds
// This constant converts seconds to milliseconds
const SECONDS_TO_MS = 1000;

// Buffer time in milliseconds before the actual token expiry when we consider it expired
// Adding a small buffer helps prevent edge cases where the token expires during processing
const TOKEN_EXPIRY_BUFFER_MS = 1000;

const handleTokenExpired = async (keycloakInstance: Keycloak, redirectUri: string): Promise<void> => {
    try {
        const isTokenExpired =
            keycloakInstance.refreshTokenParsed?.exp &&
            keycloakInstance.refreshTokenParsed.exp * SECONDS_TO_MS < Date.now() + TOKEN_EXPIRY_BUFFER_MS;

        if (isTokenExpired) {
            console.error("Refresh token is expired");
            keycloakInstance.clearToken();
            window.location.href = redirectUri;
        } else {
            await keycloakInstance
                .updateToken(TOKEN_REFRESH_INTERVAL_MS)
                .then((refreshed) => {
                    if (refreshed) {
                        console.debug("Token successfully refreshed");
                    } else {
                        console.debug("Token is still valid, no need to refresh.");
                    }
                })
                .catch(async () => {
                    console.error("Failed to refresh the token, or the session has expired");
                    keycloakInstance.clearToken();
                    await keycloakInstance.logout({ redirectUri });
                });
        }
    } catch (err) {
        console.error("Failed to refresh token", err);
        keycloakInstance.clearToken();
        await keycloakInstance.logout({ redirectUri });
    }
};

const initializeKeycloak = async (): Promise<Keycloak> => {
    const config = await loadStoredOrFetchConfiguration();
    const env = config.environment as "pg" | "qa" | "prod";
    const keycloakConfig = isHostControl() ? controlKeycloakConfig[env] : cockpitKeycloakConfig[env];

    const keycloakInstance = new Keycloak(keycloakConfig);
    const locale = isCockpitItaly() ? "it" : "de";
    const redirectUri = getRedirectUri(locale);

    await keycloakInstance.init({
        onLoad: "check-sso",
        scope: "email roles profile web-origins meinsenec",
        pkceMethod: "S256",
        checkLoginIframe: false,
        enableLogging: true,
    });

    keycloakInstance.onTokenExpired = () => handleTokenExpired(keycloakInstance, redirectUri);

    keycloakInstance.onAuthRefreshError = () => {
        console.error("Auth refresh error, logging out");
        keycloakInstance.clearToken();
        keycloakInstance.logout({ redirectUri });
    };

    keycloakInstance.onAuthLogout = () => {
        console.debug("User logged out");
        keycloakInstance.clearToken();
    };

    return keycloakInstance;
};

/**
 * Returns the default keycloak client or initializes a new client if none exists.
 * This automatically checks the SSO, whether the user is authenticated already.
 * Refreshes token 60 seconds before it expires.
 * If refresh of token fails, user is logged out.
 */
const getInitializedClient = async (): Promise<Keycloak> => {
    if (keycloak) return keycloak;

    try {
        keycloak = await initializeKeycloak();
        return keycloak;
    } catch (e) {
        console.error("Error initializing keycloak:", e);
        throw e;
    }
};

export const clientProvider: KeycloakClientProvider = {
    isInitialized: () => !!keycloak,
    getInitializedClient,
};
